import { Box, Chip } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import Toolbar, { ToolbarProps } from '@mui/material/Toolbar';
import { FormattedMessage } from 'react-intl';
import StepToolbar from '~/components/toolbar';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import AppConfig from '~/utils/appconfig';
import WorkspaceHorizonLogo from '../Logo/WorkspaceHorizonLogo';
import SaveStatusIndicator from '../SaveChanges/SaveStatusIndicator';
import ApplicationQuickMenu from './ApplicationQuickMenu';
import BackToPatientListButton from './BackToPatientListButton';
import PatientMenu from './PatientMenu';
import SignedInAccount from './SignedInAccount';

/**
 * Fixed height of the app header; matches the theme's MuiToolbar minHeight
 * override, so content below offsets with a `<Toolbar variant="dense" />`
 * spacer.
 */
const HEADER_HEIGHT_PX = 56;

export default function Header({ ...toolbarProps }: ToolbarProps) {
    const selectedPatient = useSelectedPatient();

    return (
        <AppBar
            elevation={0}
            sx={{
                zIndex: (theme) => theme.zIndex.drawer + 1,
                background: (theme) => theme.palette.background.default,
                height: HEADER_HEIGHT_PX,
                minHeight: HEADER_HEIGHT_PX,
            }}
        >
            <Toolbar
                {...toolbarProps}
                variant="dense"
                sx={{
                    display: 'flex',
                    gap: 1,
                    mx: 1,
                    ml: 1.5,
                    height: HEADER_HEIGHT_PX,
                    minHeight: HEADER_HEIGHT_PX,
                }}
            >
                <Box
                    flexGrow="1"
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Box display="flex" alignItems="center" gap={2}>
                        <Box display="flex" alignItems="center" gap={1}>
                            <WorkspaceHorizonLogo height={26} />
                        </Box>
                        <BackToPatientListButton />
                        {selectedPatient && (
                            <PatientMenu patient={selectedPatient} />
                        )}
                        <SaveStatusIndicator />
                        <StepToolbar />
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                        {!AppConfig.clinicalUse && (
                            <Chip
                                variant="outlined"
                                size="small"
                                color="warning"
                                sx={{
                                    px: 0.5,
                                    minHeight: 0,
                                    height: 20,
                                    fontSize: 11,
                                }}
                                label={
                                    <FormattedMessage id="notForClinicalUse" />
                                }
                            />
                        )}
                        <SignedInAccount />
                        <ApplicationQuickMenu />
                    </Box>
                </Box>
            </Toolbar>
        </AppBar>
    );
}
